import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const startedRef = useRef(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (startedRef.current) return;   // React runs effects twice in dev; the token is single-use
    startedRef.current = true;
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }
    
    verifyEmail(token);
  }, [searchParams]);
  
  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify-email`, {
        params: { token }
      });
      
      // Clear any existing auth data to prevent session confusion
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      setStatus('success');
      setMessage(response.data?.message || 'Email verified successfully! You can now log in.');
      setTimeout(() => {
        navigate('/', { state: { verified: true, message: 'Email verified — you can log in now.' } });
      }, 2500);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900 to-pink-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Email Verification</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'verifying' && (
            <div className="py-8">
              <Loader2 className="w-16 h-16 mx-auto text-cyan-500 animate-spin mb-4" />
              <p className="text-lg text-gray-600">Verifying your email...</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-green-600 mb-2">Success!</h3>
              <p className="text-gray-600 mb-2">{message}</p>
              <p className="text-sm text-gray-500 mb-6">Taking you to the login page…</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-cyan-500 to-purple-500"
              >
                Go to Login Now
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="py-8">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h3 className="text-xl font-semibold text-red-600 mb-2">Verification Failed</h3>
              <p className="text-gray-600 mb-6">{message}</p>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
              >
                Back to Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
 VerifyEmailPage;
